import { createAsyncThunk, createEntityAdapter, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit"
import { AppDataState, AppThunkAPIType, unknownError } from "../../app/appTypes"
import { RootState } from "../../app/store"
import { getWithAuth, postWithAuth } from "../../http"
import {
    clearPendingChanges as clearPendingChangesInDb,
    getPendingChanges,
    putPendingChange,
} from "./ursOfferPersistentStore"

export type NoteHistory = {
    blockKey: number
    currentNote: Note | null
    history: Note[]
}

export interface Note {
    id: number
    blockKey: string
    documentId: number
    importedDocumentElementId: number
    note: string
    comment: string
    createdBy: string
    createdByFullName: string
    createDate: string
}

const adapter = createEntityAdapter<NoteHistory>({
    selectId: note => note.blockKey
})


export type PendingNoteChange = {
    blockKey: string
    elementId: string | null
    note: string
    comment: string
}

export interface UrsOfferNotesState {
    docId: number | null
    notes: EntityState<NoteHistory>
    loaded: AppDataState
    pendingChanges: Record<string, PendingNoteChange>
}

const initialState: UrsOfferNotesState = {
    docId: null,
    notes: adapter.getInitialState(),
    loaded: { type: 'empty' },
    pendingChanges: {},
}

// Private thunk — only the HTTP call; used in extraReducers below
const loadUrsOfferNotesHttp = createAsyncThunk<NoteHistory[], { docId: number }, AppThunkAPIType>(
    "urs-offer-notes/load-all-http",
    async ({ docId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/imported-documents/${docId}/notes`
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as NoteHistory[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const postUrsOfferNote = createAsyncThunk<NoteHistory, PostUrsOfferCommentArg, AppThunkAPIType>(
    "urs-offer-notes/post",
    async ({ docId, ...arg }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/imported-documents/${docId}/block/${arg.blockKey}/note`,
            payload: arg,
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as NoteHistory
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export interface PostUrsOfferCommentArg {
    docId: number
    blockKey: string
    elementId: string | null
    note: string
    comment: string
}

const ursOfferNotes = createSlice({
    name: 'ursOfferNotes',
    initialState,
    reducers: {
        _setLocalNote(state, action: PayloadAction<PendingNoteChange>) {
            const { blockKey } = action.payload
            state.pendingChanges[blockKey] = action.payload
        },
        clearPendingChanges(state) {
            state.pendingChanges = {}
        },
        restorePendingChanges(state, action: PayloadAction<PendingNoteChange[]>) {
            for (const change of action.payload) {
                if (!state.pendingChanges[change.blockKey]) {
                    state.pendingChanges[change.blockKey] = change
                }
            }
        },
    },
    extraReducers: builder => {
        builder.addCase(loadUrsOfferNotesHttp.pending, (state, action) => {
            state.docId = action.meta.arg.docId
            state.loaded = {
                type: 'loading',
                requestId: action.meta.requestId
            }
        })
        builder.addCase(loadUrsOfferNotesHttp.rejected, (state, action) => {
            state.loaded = {
                type: 'error',
                error: action.payload ?? unknownError(),
            }
        })
        builder.addCase(loadUrsOfferNotesHttp.fulfilled, (state, action) => {
            state.loaded = { type: 'loaded' }
            adapter.setAll(state.notes, action.payload)
        })
        builder.addCase(postUrsOfferNote.fulfilled, (state, action) => {
            adapter.upsertOne(state.notes, action.payload)
        })
    },
})

// Public thunks — defined after slice so they can reference slice action creators

export const setLocalNote = createAsyncThunk<void, PendingNoteChange, AppThunkAPIType>(
    "urs-offer-notes/set-local-note",
    async (change, { dispatch, getState }) => {
        dispatch(ursOfferNotes.actions._setLocalNote(change))
        const { docId } = (getState() as RootState).ursOfferNotes
        if (docId) {
            putPendingChange(docId, change).catch(console.error)
        }
    }
)

export const saveAllPendingNotes = createAsyncThunk<void, number, AppThunkAPIType>(
    "urs-offer-notes/save-all-pending",
    async (docId, { dispatch, getState }) => {
        const state = getState() as RootState
        const pending = Object.values(state.ursOfferNotes.pendingChanges)
        await Promise.all(
            pending.map(change =>
                dispatch(postUrsOfferNote({
                    docId,
                    blockKey: change.blockKey,
                    elementId: change.elementId,
                    note: change.note,
                    comment: change.comment,
                })).unwrap()
            )
        )
        dispatch(ursOfferNotes.actions.clearPendingChanges())
        clearPendingChangesInDb(docId).catch(console.error)
    }
)

export const loadUrsOfferNotes = createAsyncThunk<void, { docId: number }, AppThunkAPIType>(
    "urs-offer-notes/load-all",
    async ({ docId }, { dispatch }) => {
        await dispatch(loadUrsOfferNotesHttp({ docId })).unwrap()
        const changes = await getPendingChanges(docId)
        if (changes.length > 0) {
            dispatch(ursOfferNotes.actions.restorePendingChanges(changes))
        }
    }
)

export const { clearPendingChanges, restorePendingChanges } = ursOfferNotes.actions

export const selectUrsOfferNotes = (state: RootState) => state.ursOfferNotes

export const selectPendingChanges = (state: RootState) => state.ursOfferNotes.pendingChanges

export const {
    selectAll: selectAllUrsOfferNotes,
    selectById: selectUrsOfferNoteById,
} = adapter.getSelectors((state: RootState) => selectUrsOfferNotes(state).notes)

export default ursOfferNotes.reducer
